import { BACKEND_CLIENT_VERSION } from './generated/backend-client-version.js';
import {
  ApiRouteUnauthenticatedPolicy,
  ApiRouteVisibility,
  ApiCallMethod,
  type ApiAuthResolver,
  type ApiParamsInput,
  type ApiParamsRecord,
  type ApiQueryInput,
  type ApiTransport,
  type FunctionizeConfig,
  type InTypeApi,
  type VersionDeviation,
} from './types.js';
import { checkDeviation } from './versioning.js';

interface ApplyAuthInput {
  accessToken?: string;
}

export enum ApiRouteAuthErrorCode {
  AuthRequired = 'auth_required',
  UnauthenticatedSkipped = 'unauthenticated_skipped',
}

export class ApiRouteAuthError extends Error {
  readonly code: ApiRouteAuthErrorCode;

  constructor(input: { code: ApiRouteAuthErrorCode; message: string }) {
    super(input.message);
    this.name = 'ApiRouteAuthError';
    this.code = input.code;
  }
}

export interface ApiWrapperParams {
  backendUrl: string;
  accessToken?: string;
  /**
   * Optional shared-secret service token. When set, the wrapper adds
   * `X-Service-Token: <token>` to every request and does NOT set
   * a user bearer token (service-to-service callers identify the user via
   * body/query fields, not via header). Used by `bl → billing` calls.
   */
  serviceToken?: string;
  frontEndVersion: string;
  transport: ApiTransport;
  authResolver?: ApiAuthResolver;
  logger?: Pick<Console, 'warn'>;
}

interface ResolvedRouteAuth {
  visibility: ApiRouteVisibility;
  unauthenticatedPolicy: ApiRouteUnauthenticatedPolicy;
}

export class ApiWrapper {
  private backendUrl: string;
  private accessToken?: string;
  private readonly serviceToken?: string;
  private readonly backendClientVersion: string;
  private readonly frontEndVersion: string;
  private readonly transport: ApiTransport;
  private readonly authResolver?: ApiAuthResolver;
  private readonly logger: Pick<Console, 'warn'>;
  private deviation: VersionDeviation;

  constructor({
    backendUrl,
    accessToken,
    serviceToken,
    frontEndVersion,
    transport,
    authResolver,
    logger,
  }: ApiWrapperParams) {
    this.backendUrl = backendUrl.replace(/\/+$/, '');
    this.accessToken = accessToken;
    this.serviceToken = serviceToken;
    this.backendClientVersion = BACKEND_CLIENT_VERSION;
    this.frontEndVersion = frontEndVersion;
    this.transport = transport;
    this.authResolver = authResolver;
    this.logger = logger ?? console;

    this.deviation = this.verifyVersionCompatibility();
  }

  applyAuth(input: ApplyAuthInput): void {
    this.accessToken = input.accessToken;
  }

  getVersionDeviation(): VersionDeviation {
    return this.deviation;
  }

  functionize<
    P extends ApiParamsInput | undefined,
    Q extends ApiQueryInput | undefined,
    B,
    TOutput = unknown,
  >(
    config: FunctionizeConfig<P, Q, B> & { readonly _response?: TOutput },
  ): (input?: InTypeApi<P, Q, B>) => Promise<TOutput> {
    return async (input?: InTypeApi<P, Q, B>): Promise<TOutput> => {
      const mergedParams = this.mergeParams({
        defaultParams: config.defaultParams,
        inputParams: input?.params,
      });
      const url = this.buildUrl({
        routeTemplate: config.routeTemplate,
        params: mergedParams,
      });

      if ((config.method === ApiCallMethod.Get || config.method === ApiCallMethod.Delete) && input?.body !== undefined) {
        throw new Error(
          `Body is not allowed for ${config.method} ${config.routeTemplate}. Use query for GET/DELETE requests.`,
        );
      }

      const accessToken = await this.resolveAccessToken();
      this.assertRouteAuth({
        config,
        accessToken,
      });

      const headers = this.buildHeaders({
        customHeaders: input?.headers,
        body: input?.body,
        accessToken,
      });

      return this.transport.request<TOutput, B, Q>({
        method: config.method,
        url,
        headers,
        query: input?.query,
        body: input?.body,
        signal: input?.signal,
      });
    };
  }

  private verifyVersionCompatibility(): VersionDeviation {
    const deviation = checkDeviation(this.backendClientVersion, this.frontEndVersion);

    if (deviation.breakingMismatch) {
      throw new Error(
        `Breaking API client version mismatch. Frontend version ${deviation.frontEndVersion} is incompatible with backend version ${deviation.backendVersion}.`,
      );
    }

    if (deviation.smallMismatch) {
      this.logger.warn(
        `[ApiWrapper] Minor API client mismatch: frontend=${deviation.frontEndVersion}, backend=${deviation.backendVersion}. Review client changelog and update soon.`,
      );
    }

    return deviation;
  }

  private buildUrl(input: {
    routeTemplate: string;
    params: ApiParamsRecord;
  }): string {
    const replacedRoute = input.routeTemplate.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => {
      const value = input.params[key];
      if (value === undefined || value === null) {
        throw new Error(`Missing required route param '${key}' for route '${input.routeTemplate}'.`);
      }
      return encodeURIComponent(String(value));
    });

    if (/:([A-Za-z0-9_]+)/.test(replacedRoute)) {
      throw new Error(`Route template '${input.routeTemplate}' still contains unresolved params.`);
    }

    if (replacedRoute.startsWith('http://') || replacedRoute.startsWith('https://')) {
      return replacedRoute;
    }

    return `${this.backendUrl}${replacedRoute}`;
  }

  private buildHeaders(input: {
    customHeaders?: Record<string, string>;
    body: unknown;
    accessToken?: string;
  }): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(input.customHeaders ?? {}),
    };

    if (typeof FormData !== 'undefined' && input.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    if (this.serviceToken) {
      headers['X-Service-Token'] = this.serviceToken;
      return headers;
    }

    if (input.accessToken) {
      headers['Authorization'] = `Bearer ${input.accessToken}`;
    }

    return headers;
  }

  private async resolveAccessToken(): Promise<string | undefined> {
    if (!this.authResolver) {
      return this.accessToken;
    }

    const resolvedAccessToken = await this.authResolver({
      currentAccessToken: this.accessToken,
    });
    this.accessToken = resolvedAccessToken;
    return this.accessToken;
  }

  private assertRouteAuth(input: {
    config: Pick<
      FunctionizeConfig<ApiParamsInput | undefined, ApiQueryInput | undefined, unknown>,
      'method' | 'routeTemplate' | 'auth'
    >;
    accessToken?: string;
  }): void {
    const routeAuth = this.resolveRouteAuth({
      config: input.config,
    });

    // Service-token wrappers satisfy the auth check on their own.
    if (routeAuth.visibility === ApiRouteVisibility.Public || input.accessToken || this.serviceToken) {
      return;
    }

    const errorMessage = `Authentication required for ${input.config.method} ${input.config.routeTemplate}`;
    if (routeAuth.unauthenticatedPolicy === ApiRouteUnauthenticatedPolicy.Skip) {
      this.logger.warn(`[ApiWrapper] Skipping unauthenticated private route ${input.config.method} ${input.config.routeTemplate}`);
      throw new ApiRouteAuthError({
        code: ApiRouteAuthErrorCode.UnauthenticatedSkipped,
        message: errorMessage,
      });
    }

    throw new ApiRouteAuthError({
      code: ApiRouteAuthErrorCode.AuthRequired,
      message: errorMessage,
    });
  }

  private resolveRouteAuth(input: {
    config: Pick<
      FunctionizeConfig<ApiParamsInput | undefined, ApiQueryInput | undefined, unknown>,
      'auth'
    >;
  }): ResolvedRouteAuth {
    return {
      visibility: input.config.auth?.visibility ?? ApiRouteVisibility.Private,
      unauthenticatedPolicy: input.config.auth?.unauthenticatedPolicy ?? ApiRouteUnauthenticatedPolicy.Throw,
    };
  }

  private mergeParams(input: {
    defaultParams?: ApiParamsInput;
    inputParams?: ApiParamsInput;
  }): ApiParamsRecord {
    return {
      ...(this.toParamsRecord(input.defaultParams)),
      ...(this.toParamsRecord(input.inputParams)),
    };
  }

  private toParamsRecord(input?: ApiParamsInput): ApiParamsRecord {
    if (!input) {
      return {};
    }
    return input as ApiParamsRecord;
  }
}
